import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUnreadCount, fetchNotifications } from 'store/features/notificationSlice';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { Box, Text } from '@mantine/core';
import { IconBellRinging } from '@tabler/icons-react';
import { semanticColors } from 'theme/semanticColors';

const POLL_INTERVAL = 30_000;

// -- Custom snackbar content for new-notification toast --
const NewNotificationSnackbar = forwardRef(({ id, count }, ref) => {
  const label = count === 1 ? 'New notification' : `${count} new notifications`;

  return (
    <Box
      ref={ref}
      onClick={() => closeSnackbar(id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        backgroundColor: semanticColors.surface.raised,
        borderRadius: 8,
        borderLeft: '4px solid #f05776',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
        padding: '10px 16px',
        minWidth: 260,
        maxWidth: 340,
        maxHeight: 64,
        cursor: 'pointer'
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: 'rgba(240, 87, 118, 0.1)',
          flexShrink: 0
        }}
      >
        <IconBellRinging size={20} color="#f05776" />
      </Box>
      <Box style={{ minWidth: 0 }}>
        <Text
          style={{
            fontSize: '0.84rem',
            fontWeight: 600,
            lineHeight: 1.3,
            color: semanticColors.text.primary,
            letterSpacing: '-0.01em'
          }}
        >
          {label}
        </Text>
        <Text
          style={{
            fontSize: '0.72rem',
            fontWeight: 400,
            lineHeight: 1.3,
            color: semanticColors.text.muted,
            marginTop: 2
          }}
        >
          Tap the bell to view
        </Text>
      </Box>
    </Box>
  );
});

NewNotificationSnackbar.displayName = 'NewNotificationSnackbar';
NewNotificationSnackbar.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  count: PropTypes.number.isRequired
};

// -- Hook --
const useNotificationPolling = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const { unreadCount, filter } = useSelector((state) => state.notification);
  const intervalRef = useRef(null);
  const prevCountRef = useRef(null);

  // Track count changes — show snackbar and refresh the notification list
  useEffect(() => {
    if (prevCountRef.current !== null && unreadCount > prevCountRef.current) {
      const diff = unreadCount - prevCountRef.current;

      enqueueSnackbar({
        autoHideDuration: 4000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        content: (key) => <NewNotificationSnackbar id={key} count={diff} />
      });

      // Refresh the notification list so it's up to date when the user opens the bell
      dispatch(fetchNotifications({ filter }));
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount, dispatch, filter]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const poll = () => dispatch(fetchUnreadCount());

    // Initial fetch
    poll();

    const startPolling = () => {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(poll, POLL_INTERVAL);
      }
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        poll();
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    // eslint-disable-next-line consistent-return
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [dispatch, isLoggedIn]);
};

export default useNotificationPolling;
